/* -------------------------- Design imports start -------------------------- */
import LoadingButton from '@mui/lab/LoadingButton'
import { Box, Button, Grid, Typography } from '@mui/material'
/* --------------------------- Design imports end --------------------------- */
/* ------------------------ Functional imports start ------------------------ */
import React, { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import FileInput from '../../../../../../components/inputs/FileInput'
import MultilevelDrawer from '../../../../../../components/layout/MultilevelDrawer'
import InputFileList from '../../../../../../components/widgets/InputFileList'
import FilesDrawer from '../../../../../Filesystem/components/FilesDrawer'
import LogTool from '../../../../../../logger/logTools'
import { type FileInfo, type FileUploadStatus } from '../../../../../../utils/types'
import { Form } from '../../../../../Form'
import GetConstant from '../../../../utils/constants'
import { deleteFileFromUrl, uploadFileToUrl, fetchFileList } from '../../../../../Filesystem/api/fileApi'
import useFetchExistingFiles from '../../../../../../hooks/useFetchExistingFiles'
import { deriveFilesUrl } from '../../../../../Filesystem/utils/filesHelper'
/* ------------------------- Functional imports end ------------------------- */

type Props = {
  open: boolean
  setOpen: (open: boolean) => void
  formState: {
    state: any | undefined
    setState: React.Dispatch<React.SetStateAction<any | undefined>>
  }
  onClose?: () => void
  onEditConfirm?: (formState: any) => Promise<string | void>
  onFilesUploaded?: () => void
}

/* -------------------------------------------------------------------------- */
/*                                Start Component                             */
/* -------------------------------------------------------------------------- */
export default function RequestStaffDrawer(props: Props) {
  /* -------------------------- Non state data start -------------------------- */
  const { open, setOpen, formState, onClose = () => null, onEditConfirm = () => null, onFilesUploaded = () => null } = props
  const { t } = useTranslation(['request', 'common'])
  const log = new LogTool({ context: 'RequestStaffDrawer', enable: true, logLevel: 'warn' })
  /* --------------------------- Non state data end --------------------------- */

  /* ---------------------------- Flag states start --------------------------- */
  /* ----------------------------- Flag states end ---------------------------- */

  /* ---------------------------- Data states start --------------------------- */
  const [fileInput, setFileInput] = useState<FileInfo[]>([])
  const [uploadedFiles, setUploadedFiles] = useState<FileInfo[]>([])
  const [waiting, setWaiting] = useState(false)
  const [openFilesDrawer, setOpenFilesDrawer] = useState(false)
  /* ----------------------------- Data states end ---------------------------- */

  /* ------------------------------ Effects start ----------------------------- */
  useEffect(() => {
    if (fileInput.length > 0 && fileInput.every(file => file.uploadStatus === 'success')) {
      setWaiting(false)
      setOpen(false)
    }
  }, [fileInput])

  // Fetch existing files when drawer opens, formState changes, or FilesDrawer opens
  useFetchExistingFiles({
    open,
    formState,
    openFilesDrawer,
    setUploadedFiles,
    logContext: 'request',
  })

  // Reset fileInput and waiting when drawer closes
  useEffect(() => {
    if (!open || !formState.state) {
      setFileInput([])
      setWaiting(false)
    }
  }, [open, formState.state])
  /* ------------------------------- Effects end ------------------------------ */

  /* ------------------------- Utility functions start ------------------------ */
  /* -------------------------- Utility functions end ------------------------- */

  /* ------------------------ Callback functions start ------------------------ */
  const handleEditConfirm = async () => {
    log.debug('handleEditRequest -> ', formState.state)
    setWaiting(true)
    // set loading state on all files to loading
    if (fileInput.length > 0) {
      setFileInput((prev: FileInfo[]) =>
        prev.map(file => Object.assign(file, { uploadStatus: 'loading' }))
      )
    }
    // posting request to server and waiting on response
    const requestFilesUrl = await onEditConfirm(formState.state)

    log.debug('requestFilesUrl -> ', requestFilesUrl)
    // check if the user specified files to upload !
    if (fileInput.length > 0 && requestFilesUrl) {
      // upload the files one by one for a more detailed feedback if something goes wrong,
      // and to make use parallel uploading to decrease upload time
      for (const file of fileInput) {
        await uploadRequestFile(file, requestFilesUrl)
        // response.articleFilesUrl cannot be undefined here as the server created the article successfully and therefore created an article files url

        // store the articleFilesUrl in the inputFiles state to have it available in case something goes wrong
        /* setInputFiles((prev: FileInfo[]) => prev.map(
          file => Object.assign(file, {uploadUrl: response.articleFilesUrl})
        )) */
      }
      
      // Refresh uploaded files list after successful upload
      if (formState.state?.self) {
        try {
          const filesUrl = deriveFilesUrl(formState.state)
          if (filesUrl) {
            const fileResponse = await fetchFileList(filesUrl)
            setUploadedFiles(fileResponse.results)
          }
        } catch (error) {
          log.error('Error refreshing uploaded files after upload', error)
        }
      }
      
      // Notify parent that files were uploaded so it can refresh FilesDrawer
      onFilesUploaded()
    } else {
      // if no files are uploaded, we can set the waiting state to false and close the drawer
      setWaiting(false)
      setOpen(false)
    }
  }

  /**
   * Setter for the uploadStatus property on a FileInfo object within this component.
   * Do not use this function if you want to update multiple files at once as the component is rerendered after every status update!
   * @param file the file on which the uploadStatus should be updated.
   * @param status the updated uploadStatus.
   */
  function setFileUploadStatus(file: FileInfo, status: FileUploadStatus) {
    setFileInput((prev: FileInfo[]) =>
      prev.map((listedFile: FileInfo) => {
        if (listedFile.name === file.name) {
          // add an upload-status of success to the file info object or overwrite it with the provided status
          return Object.assign(file, { uploadStatus: status })
        }
        return listedFile
      })
    )
  }

  /**
   * Wrapper for the uploadFile function that specifies the onSuccess and onError callbacks.
   * This function simply allows us to upload files using the same behavior at multiple places
   * within this component without rewriting the callback functions all the time.
   * @param file the file to upload.
   * @param url the url to upload the file to.
   */
  async function uploadRequestFile(file: FileInfo, url: string) {
    try {
      await uploadFileToUrl(url, file as File)
      setFileUploadStatus(file, 'success')
    } catch (error) {
      log.error('Error uploading request file', error)
      setFileUploadStatus(file, 'uploadError')
    }
  }

  /**
   * Remove a file from fileInput list before it is uploaded
   * @param file
   */
  const handleRemoveFileFromList = (file: FileInfo) => {
    // update rendered files list
    setFileInput(prevFileList => {
      // get the location of the file to remove within the files list
      const fileIndex = prevFileList.findIndex(listedFile => listedFile.name === file.name)
      // remove the file from the list. Note that this does not work on the prevFileList directly
      const updatedFileList = [...prevFileList]
      updatedFileList.splice(fileIndex, 1)

      // return the files list where the file is removed
      return updatedFileList
    })
  }
  /**
   * (Re)upload a file to the specified url.
   * @param file The file to upload
   * @param url The url to upload the file to.
   */
  const handleReUploadFile = async (file: FileInfo, url: string) => {
    // set the uploadStatus of the current file to loading
    setFileUploadStatus(file, 'loading')
    // retry to upload the file
    await uploadRequestFile(file, url)
  }

  /**
   * Delete a file that was uploaded already.
   * @param file The file to delete.
   */
  const handleDeleteFile = async (file: FileInfo): Promise<void> => {
    try {
      await deleteFileFromUrl(file.self as string)
      // Remove from uploadedFiles
      setUploadedFiles((prev) => prev.filter((f) => f.self !== file.self))
    } catch (error) {
      log.error('Error deleting request file', error)
      throw error // Re-throw so UploadedFilesTable can handle it
    }
  }

  const handleDeleteMultipleFiles = async (files: FileInfo[]): Promise<void> => {
    try {
      await Promise.all(
        files.map(async (file) => {
          if (!file.self) return
          await deleteFileFromUrl(file.self)
        })
      )
      // Remove all deleted files from uploadedFiles
      const deletedSelfs = new Set(files.map((f) => f.self).filter(Boolean))
      setUploadedFiles((prev) => prev.filter((f) => !f.self || !deletedSelfs.has(f.self)))
    } catch (error) {
      log.error('Error deleting multiple request files', error)
      throw error
    }
  }
  /* ------------------------- Callback functions end ------------------------- */

  /* ------------------------- Render constants start ------------------------- */
  const requestStaffFormFields: any = GetConstant({ name: 'requestFormFieldsStaff' })
  
  // Compute files URL for FilesDrawer
  const filesDrawerUrl = useMemo(() => {
    // Always construct the files URL directly from formState.state.self if available
    if (formState.state?.self) {
      return `${formState.state.self}files/`
    }
    // Fallback: derive URL from uploadedFiles if available
    if (uploadedFiles.length > 0 && uploadedFiles[0]?.self) {
      const fileSelf = uploadedFiles[0].self
      const trimmed = fileSelf.endsWith('/') ? fileSelf.slice(0, -1) : fileSelf
      const lastSlash = trimmed.lastIndexOf('/')
      if (lastSlash !== -1) {
        return `${trimmed.slice(0, lastSlash + 1)}`
      }
    }
    return undefined
  }, [formState.state?.self, uploadedFiles])
  /* -------------------------- Render constants end -------------------------- */

  /* ------------------------ Pre render actions start ------------------------ */
  /* ------------------------- Pre render actions end ------------------------- */

  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */
  return (
    <>
      <MultilevelDrawer
        open={open}
        setOpen={setOpen}
        onClose={onClose}
        size="big"
        title={t('request:content.label.editRequest') || ''}
        customActions={
          <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
            <Button
              id="cancelButton"
              onClick={() => setOpen(false)}
              color="inherit"
              variant="contained"
              sx={{ mr: 1, textTransform: 'none' }}
            >
              {t('common:interaction.button.cancel')}
            </Button>
            <LoadingButton
              id="createButton"
              variant="contained"
              sx={{ marginLeft: '10px', color: 'whitesmoke', textTransform: 'none' }}
              loading={waiting}
              onClick={handleEditConfirm}
            >
              {t('common:interaction.button.save')}
            </LoadingButton>
          </Box>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Form
              editing={true}
              formFields={requestStaffFormFields}
              formObject={formState.state}
              onChange={(value: any) => {
                formState.setState(value)
              }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '15px', marginBottom: '10px' }}>
              <Typography variant="h6">
                {t('common:content.label.documents')}
              </Typography>
              
              {/* Already Uploaded Files - Button to open FilesDrawer */}
              {uploadedFiles.length > 0 && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setOpenFilesDrawer(true)}
                  sx={{ 
                    textTransform: 'none',
                    color: 'white',
                    display: 'flex',
                  }}
                >
                  {t('common:content.label.viewUploadedFiles', { count: uploadedFiles.length }) || 
                   `${t('common:content.label.uploadedFiles')} (${uploadedFiles.length})`}
                </Button>
              )}
            </Box>
            <FileInput
              inputState={{ state: fileInput, setState: setFileInput }}
              maxAllowedFiles={10}
            />
            {fileInput.length > 0 && (
              <InputFileList
                files={fileInput}
                noFilesInfo=""
                enableDownloadFile={false}
                onRemoveFileFromList={handleRemoveFileFromList}
                onReUploadFile={handleReUploadFile}
                onDeleteFile={handleDeleteFile}
              />
            )}
          </Grid>
        </Grid>
      </MultilevelDrawer>
      <FilesDrawer
        open={openFilesDrawer}
        setOpen={setOpenFilesDrawer}
        files={uploadedFiles.length > 0 ? uploadedFiles : filesDrawerUrl}
        title={t('common:content.label.uploadedFiles')}
        size="big"
        refreshKey={uploadedFiles.length}
      />
    </>
  )
}
